import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAcraEntityHintsByUens } from '../lib/acraEntitiesDb';
import { resolveAcraStatusForClient } from '../lib/acraRegistry';
import { isUenMarkedNa } from '../lib/uenValue';
import type { Client } from '../types/crm';
import { useAcraSyncRealtime } from './useAcraSyncRealtime';

export type AccountAcraLookup = {
  statusMap: Map<string, string>;
  registeredAddressMap: Map<string, string>;
};

const EMPTY_LOOKUP: AccountAcraLookup = {
  statusMap: new Map(),
  registeredAddressMap: new Map(),
};

export function useAccountAcraStatusMap(clients: Client[]): AccountAcraLookup {
  const uens = useMemo(
    () =>
      clients
        .map((client) => client.uen?.trim().toUpperCase() ?? '')
        .filter((uen) => Boolean(uen) && !isUenMarkedNa(uen)),
    [clients],
  );
  const uenKey = uens.join('\u0001');
  const [lookup, setLookup] = useState<AccountAcraLookup>(() => EMPTY_LOOKUP);
  const [refreshToken, setRefreshToken] = useState(0);

  const reload = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  useAcraSyncRealtime(reload, { onlyOnComplete: true, debounceMs: 2000 });

  useEffect(() => {
    if (!uenKey) {
      setLookup(EMPTY_LOOKUP);
      return;
    }

    let cancelled = false;
    void getAcraEntityHintsByUens(uenKey.split('\u0001'))
      .then((hints) => {
        if (cancelled) return;
        const statusMap = new Map<string, string>();
        const registeredAddressMap = new Map<string, string>();
        for (const [uen, hint] of hints) {
          if (hint.status) statusMap.set(uen, hint.status);
          if (hint.registeredAddress) registeredAddressMap.set(uen, hint.registeredAddress);
        }
        setLookup({ statusMap, registeredAddressMap });
      })
      .catch(() => {
        if (!cancelled) setLookup(EMPTY_LOOKUP);
      });

    return () => {
      cancelled = true;
    };
  }, [uenKey, refreshToken]);

  return lookup;
}

export function acraStatusForClient(
  client: Client,
  statusMap: Map<string, string>,
): string | null {
  return resolveAcraStatusForClient(client, statusMap);
}
